/** Which tag values are chosen, by key. Keys with no values are absent rather than empty. */
export type TagSelection = Record<string, readonly string[]>;

/**
 * Ingest replaces these on every re-run, so a person's edit to one would be overwritten the next
 * time it runs. Kept here, rather than beside the editor that reads it, because the picker's
 * grouping (`isIngestOwned`) and the tag editor's own reserved-key check both need the same list,
 * and two copies would drift apart the moment one of them is updated for a new ingest field.
 *
 * Deliberately duplicated from the server's `MODEL_OWNED_TAG_KEYS`
 * (`apps/guidelines-api/src/brand-media/ingestion/media-description.schema.ts`) rather than
 * imported: the two are separate deployables. A change to one should prompt checking the other.
 */
/* eslint-disable i18next/no-literal-string -- tag keys, not copy */
export const INGEST_OWNED_TAG_KEYS = [
  'subject',
  'surface',
  'activity',
  'adUsage',
  'object',
] as const;
/* eslint-enable i18next/no-literal-string */

export const isIngestOwned = (key: string): boolean =>
  (INGEST_OWNED_TAG_KEYS as readonly string[]).includes(key);

/**
 * A client-side preview of the server's `normaliseTag`
 * (`apps/guidelines-api/src/brand-media/tag-normalise.ts`). Used only to show what a typed
 * key/value will collapse to before a request is sent, so a person never sees "added" for
 * something the server would have silently dropped (e.g. a key of only punctuation).
 *
 * Deliberately duplicated rather than imported, for the same reason as `INGEST_OWNED_TAG_KEYS`
 * above: separate deployables. Keep the two regexes in sync by hand.
 */
export const previewNormaliseTag = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * A key the way the server will store it, which is not simply the value rule applied to a key.
 *
 * `previewNormaliseTag` lowercases and the ingest vocabulary is camelCase, so previewing a key with
 * it turns `adUsage` into `adusage` -- a name nothing recognises as owned. That is how the reserved
 * key check came to be passable: a person typing one of the four got past it and created a second
 * jsonb key holding the same facet, which the library then drew as two pills of the same word.
 *
 * Matched on letters and digits alone, so the hyphens the value rule inserts cannot make a second
 * key either: `adusage`, `Ad Usage` and `ad-usage` are all `adUsage`.
 *
 * Mirrors `normaliseTagKey` (`apps/guidelines-api/src/brand-media/tag-normalise.ts`), duplicated
 * for the reason the value rule above is: separate deployables, kept in step by hand.
 */
export const previewNormaliseTagKey = (raw: string): string => {
  const flattened = previewNormaliseTag(raw);
  if (flattened === '') return '';
  const letters = flattened.replace(/-/g, '');
  return INGEST_OWNED_TAG_KEYS.find((key) => key.toLowerCase() === letters) ?? flattened;
};

/**
 * Adds or removes one value, leaving every other key alone.
 *
 * A key loses its entry entirely when its last value goes, rather than holding an empty array: an
 * empty key would key a different request from no key at all and refetch the same answer under a
 * second cache entry.
 */
export const toggleTag = (selection: TagSelection, key: string, value: string): TagSelection => {
  const current = selection[key] ?? [];
  const next = current.includes(value)
    ? current.filter((held) => held !== value)
    : [...current, value];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [key]: _, ...rest } = selection;
  return next.length === 0 ? rest : { ...rest, [key]: next };
};

/** The `key:value` pairs the endpoint takes. */
export const tagQueryPairs = (selection: TagSelection): string[] =>
  Object.entries(selection).flatMap(([key, values]) => values.map((value) => `${key}:${value}`));

export const isTagSelected = (selection: TagSelection, key: string, value: string): boolean =>
  (selection[key] ?? []).includes(value);
